import {
  Col,
  Divider,
  Form,
  Image,
  List,
  Modal,
  notification,
  Row,
  Segmented,
  Select,
  Space,
  Upload,
} from 'antd';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MoonOutlined, SunOutlined } from '@ant-design/icons';
import './index.css';
import google from '@/assets/google.svg';
import microsoft from '@/assets/microsoft.svg';
import facebook from '@/assets/facebook.svg';
import { useRequest } from 'ahooks';
import { updateUserProfile } from '@/services/user.js';
import {
  setThemeMode,
  updateUserProfileState,
  getUserProfileThunk,
} from '@/store/modules/globalSlice.js';
import dayjs from 'dayjs';
import { getAvatarSignedUrl, uploadToS3WithCache } from '@/services/attachment.js';
import CustomAvatar from '@/components/CustomAvatar/index.js';
import { useTranslation } from 'react-i18next';

// const OPTIONS = ['General', 'Account', 'Login History'];
const TAB_KEYS = {
  GENERAL: 'general',
  ACCOUNT: 'account',
  LOGIN_HISTORY: 'loginHistory',
};

const LANGUAGE = [
  {
    value: 'en-US',
    label: 'English',
  },
  {
    value: 'zh-TW',
    label: '繁體中文',
  },
  {
    value: 'ja-JP',
    label: '日本語',
  },
];

const THIRD_APP = {
  google,
  microsoft,
  facebook,
};

const Setting = props => {
  const { isModalOpen, setIsModalOpen } = props;
  const { i18n, t } = useTranslation('setting');
  const APPEARANCE = [
    {
      value: 'light',
      label: (
        <Space>
          <SunOutlined />
          {t('setting:light')}
        </Space>
      ),
    },
    {
      value: 'dark',
      label: (
        <Space>
          <MoonOutlined />
          {t('setting:dark')}
        </Space>
      ),
    },
  ];

  const OPTIONS = [
    { label: t('tabs.general'), value: TAB_KEYS.GENERAL },
    { label: t('tabs.account'), value: TAB_KEYS.ACCOUNT },
    { label: t('tabs.loginHistory'), value: TAB_KEYS.LOGIN_HISTORY },
  ];
  const [currentTab, setCurrentTab] = useState(TAB_KEYS.GENERAL);

  const { userProfile } = useSelector(state => state.global);

  const dispatch = useDispatch();

  const { run } = useRequest(updateUserProfile, {
    manual: true,
  });

  const handleCancel = () => {
    setIsModalOpen(false);
    setCurrentTab(TAB_KEYS.GENERAL);
  };

  const renderGeneral = () => {
    return (
      <>
        <Form.Item name="appearance" label={t('appearance')} style={{ width: '100%' }}>
          <Select options={APPEARANCE} className="setting-item-right" />
        </Form.Item>
        <Divider />
        <Form.Item name="language" label={t('language')} style={{ width: '100%' }}>
          <Select options={LANGUAGE} className="setting-item-right" />
        </Form.Item>
        <Divider />
      </>
    );
  };

  const handleFileUpload = async fileInfo => {
    const { file } = fileInfo;
    try {
      const url = await getAvatarSignedUrl(file);
      await uploadToS3WithCache(url, file);
      dispatch(getUserProfileThunk());
      notification.success({ message: 'Upload successfully' });
    } catch (error) {
      notification.error({ message: error.message });
    }
  };

  const renderAccount = () => {
    return (
      <>
        <Form.Item name="avatar" label={t('avatar')} style={{ width: '100%' }}>
          <Upload
            name="avatar"
            showUploadList={false}
            accept=".jpg,.jpeg,.png"
            className="setting-item-right-account"
            customRequest={handleFileUpload}
          >
            <CustomAvatar size={40} />
          </Upload>
        </Form.Item>
        <Divider />
        <Form.Item label={t('username')} style={{ width: '100%' }}>
          <span className="setting-item-right-account">{userProfile.name}</span>
        </Form.Item>
        <Divider />
        <Form.Item label={t('email')} style={{ width: '100%' }}>
          <span className="setting-item-right-account">{userProfile.email}</span>
        </Form.Item>
        <Divider />
        <Form.Item label={t('thirdPartyApp')} style={{ width: '100%' }}>
          <span className="setting-item-right-account">
            <Image width={80} src={THIRD_APP[userProfile.source]} preview={false} />
          </span>
        </Form.Item>
        <Divider />
      </>
    );
  };

  const renderLoginHistory = () => {
    return (
      <List
        style={{ width: '95%', margin: '0 auto', textAlign: 'center' }}
        dataSource={userProfile?.logHistory}
        renderItem={item => (
          <List.Item>
            <Row style={{ width: '100%' }} justify="space-around">
              <Col span={7}>{item.deviceInfo}</Col>
              <Col span={7}>{item.ipCountryCode}</Col>
              <Col span={7}>{dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Col>
            </Row>
          </List.Item>
        )}
      />
    );
  };

  const renderContent = () => {
    switch (currentTab) {
      case TAB_KEYS.GENERAL:
        return renderGeneral();
      case TAB_KEYS.ACCOUNT:
        return renderAccount();
      case TAB_KEYS.LOGIN_HISTORY:
        return renderLoginHistory();
      default:
        return renderGeneral();
    }
  };

  const handleOnFieldsChange = changedFields => {
    const filed = changedFields[0].name[0];
    const value = changedFields[0].value;
    const payload = {};

    if (filed === 'avatar') return;

    Object.assign(payload, { [filed]: value });
    if (filed === 'appearance') {
      dispatch(setThemeMode(value));
    }

    if (filed === 'language') {
      i18n.changeLanguage(value);
      dispatch(updateUserProfileState({ language: value }));
    }
    run(payload);
  };

  return (
    <>
      <Modal
        title={t('title')}
        width="40%"
        style={{ minWidth: '380px', top: '220px' }}
        footer={null}
        open={isModalOpen}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Segmented
          defaultChecked={TAB_KEYS.GENERAL}
          onChange={val => setCurrentTab(val)}
          style={{ width: '100%', marginBottom: '1rem' }}
          options={OPTIONS}
          block
        />
        <Form
          initialValues={{ ...userProfile, language: i18n.language }}
          size="small"
          layout="inline"
          onFieldsChange={handleOnFieldsChange}
          colon={false}
        >
          {renderContent}
        </Form>
      </Modal>
    </>
  );
};

export default Setting;
